import { toSafeHref } from '@/lib/safe-url';
import { getFileCategory } from '@/lib/file-category';
import { type DriveRow } from './drive-api';
import { downloadProtectedFile, openDocumentInNewTab } from '@/lib/file-urls';
import type { DriveFolderNode, DriveItem } from '../types';

const RANK: Record<DriveItem['type'], number> = { folder: 0, url: 1, file: 2 };

/** Folders first, then links, then files; each by name (the same order the API returns). */
export function sortDriveItems(items: DriveItem[]): DriveItem[] {
  return [...items].sort(
    (a, b) => RANK[a.type] - RANK[b.type] || a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true })
  );
}

export function toDriveItem(row: DriveRow): DriveItem {
  return { ...(row as unknown as DriveItem), created_at: new Date(row.created_at), updated_at: new Date(row.updated_at) };
}

export interface FolderOption {
  value: string;
  label: string;
}

/**
 * The Move destinations: the project's folders as an indented tree (parents before children, siblings by name), never offering
 * a folder that is being moved or lives inside one that is (the server refuses those too).
 */
export function buildFolderOptions(folders: DriveFolderNode[], movingIds: string[]): FolderOption[] {
  const moving = new Set(movingIds);
  const byParent = new Map<string | null, DriveFolderNode[]>();
  for (const folder of folders) {
    const siblings = byParent.get(folder.parent_id) || [];
    siblings.push(folder);
    byParent.set(folder.parent_id, siblings);
  }
  const options: FolderOption[] = [];
  const walk = (parentId: string | null, depth: number) => {
    const siblings = (byParent.get(parentId) || []).sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base', numeric: true }));
    for (const folder of siblings) {
      if (moving.has(folder.id)) continue;
      options.push({ value: folder.id, label: `${'    '.repeat(depth)}${folder.name}` });
      walk(folder.id, depth + 1);
    }
  };
  walk(null, 1);
  return options;
}

/** Opens a link only when it is an http(s) address; returns false when it is not safe to open. */
export function openLink(raw: string | null | undefined): boolean {
  const href = toSafeHref(raw);
  if (!href) return false;
  window.open(href, '_blank', 'noopener,noreferrer');
  return true;
}

/** Opens a file: pictures and PDFs on their permanent KrewUp page (a new tab), anything else downloads through KrewUp. */
export async function openFile(item: DriveItem): Promise<void> {
  if (!item.download_url) throw new Error('This file has no stored content');
  if (item.mime_type?.startsWith('image/') || item.mime_type === 'application/pdf') {
    openDocumentInNewTab('drive', item.id);
  } else {
    await downloadProtectedFile('drive', item.id, item.name);
  }
}

export async function downloadFile(item: DriveItem): Promise<void> {
  if (!item.download_url) throw new Error('This file has no stored content');
  await downloadProtectedFile('drive', item.id, item.name);
}

export function formatSize(bytes?: number | null): string {
  if (!bytes) return '';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  return `${Math.round((bytes / Math.pow(1024, i)) * 100) / 100} ${sizes[i]}`;
}

export const formatDate = (date: Date) => date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const TYPE_LABELS: Partial<Record<ReturnType<typeof getFileCategory>, string>> = {
  image: 'Image',
  pdf: 'PDF',
  word: 'Document',
  powerpoint: 'Document',
  text: 'Document',
  excel: 'Spreadsheet',
  video: 'Video',
};

/** The few kinds the Type column shows: Folder, Link, PDF, Image, Document, Spreadsheet, Video, File (anything else). */
export function driveTypeLabel(item: DriveItem): string {
  if (item.type === 'folder') return 'Folder';
  if (item.type === 'url') return 'Link';
  return TYPE_LABELS[getFileCategory(item.mime_type, item.extension)] || 'File';
}
